/**
 * Territory Mini Map – Circular radar-style minimap.
 * 243×243 circle, #131026 bg, 3px cyan border, dark overlay on lower portion.
 * Live player position, nearby entities, radar sweep, range rings.
 * Legend keys (Planet / Asteroid / Station) with shaped icons below.
 *
 * NOTE: We do NOT use Phaser geometry masks because they break when a
 * Container is positioned away from the world origin. Instead, all drawing
 * is clipped manually to the circle radius, and a "clip frame" covers any
 * overflow at the border.
 */

import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { Fonts, Depth } from './GameUITheme';

// ── Layout ───────────────────────────────────────────────────
const MAP_SIZE     = 243;
const MAP_RADIUS   = MAP_SIZE / 2;   // 121.5
const BG_COLOR     = 0x131026;
const BORDER_COLOR = 0x00ffff;
const BORDER_ALPHA = 0.15;
const BORDER_WIDTH = 3;

const OVERLAY_Y     = 63;
const OVERLAY_ALPHA = 0.35;
const LABEL_COLOR   = '#E0E8FF';

// Player dot
const DOT_SIZE       = 12;
const DOT_COLOR      = 0xFFFF00;
const DOT_GLOW_ALPHA = 0.15;
const DOT_GLOW_WIDTH = 9;

// Entity colours (matching Figma spec)
const ENTITY_DOT     = 4;
const COLOR_PLANET   = 0x00FFFF;
const COLOR_ASTEROID = 0xFFB842;
const COLOR_STATION  = 0x42FF90;
const COLOR_EARTH    = 0x44aaff;

// Dense asteroid belts can contain hundreds of mineable targets. Drawing each
// one on the mini-radar overwhelms ship/station readability, so regular
// asteroids are summarized into small radar-space cells.
const ASTEROID_CLUSTER_CELL = 22;
const ASTEROID_CLUSTER_MAX_VISIBLE = 36;
const ASTEROID_CLUSTER_MAX_RADIUS = 7;

// Legend row
const LEGEND_Y       = MAP_SIZE + 6;
const LEGEND_ICON    = 10;
const LEGEND_GAP     = 5;
const LEGEND_SPACING = 14;

// Viewport
const VIEWPORT_RANGE = 30;

// Radar sweep
const SWEEP_DURATION = 3000;
const SWEEP_COLOR    = 0x00ffff;

// Range rings
const RING_INTERVALS = [10, 20, 30];
const RING_COLOR     = 0x00ffff;
const RING_ALPHA     = 0.12;

// Radial lines
const RADIAL_COUNT = 8;
const RADIAL_COLOR = 0x00ffff;
const RADIAL_ALPHA = 0.08;

// Map center (slightly below geometric center to leave room for label)
const CX = MAP_RADIUS;
const CY = MAP_RADIUS + 6;
const DRAW_R = MAP_RADIUS - 15;   // usable radius for content

// ── Helpers ──────────────────────────────────────────────────

function hexToPixel(dq: number, dr: number, scale: number): { mx: number; my: number } {
  return {
    mx: CX + 1.732 * (dq + dr / 2) * scale,
    my: CY + 1.5 * dr * scale,
  };
}

function inCircle(mx: number, my: number): boolean {
  const dx = mx - CX;
  const dy = my - CY;
  return dx * dx + dy * dy < DRAW_R * DRAW_R;
}

function isPriorityAsteroid(type?: string): boolean {
  return type !== undefined && type !== 'regular';
}

// ─────────────────────────────────────────────────────────────

export class TerritoryMapPanel extends Phaser.GameObjects.Container {
  // Single graphics layer for all dynamic content
  private gfx!: Phaser.GameObjects.Graphics;

  // Animated state
  private sweepAngle = 0;
  private pulsePhase = 0;

  private mmoState: MMOStateManager;
  private zoomLevel = 1.0;

  // Legend container (repositionable)
  private legendContainer!: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene, x: number, y: number, mmoState: MMOStateManager) {
    super(scene, x, y);
    this.mmoState = mmoState;

    // Background circle (static)
    const bg = scene.add.graphics();
    bg.fillStyle(BG_COLOR, 1);
    bg.fillCircle(MAP_RADIUS, MAP_RADIUS, MAP_RADIUS);
    this.add(bg);

    // Main drawing surface
    this.gfx = scene.add.graphics();
    this.add(this.gfx);

    // Border on top of everything (acts as visual clip edge)
    const border = scene.add.graphics();
    // Thick ring that covers any overflow at the circle boundary
    border.lineStyle(BORDER_WIDTH, BORDER_COLOR, BORDER_ALPHA);
    border.strokeCircle(MAP_RADIUS, MAP_RADIUS, MAP_RADIUS - BORDER_WIDTH / 2);
    this.add(border);

    // "Map" label
    const title = scene.add.text(MAP_RADIUS, 25, 'Radar', {
      fontSize: '17px',
      color: LABEL_COLOR,
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: 2,
    });
    title.setOrigin(0.5, 0);
    this.add(title);

    // Legend
    this.buildLegend();

    // Initial draw
    this.drawAll();

    // Start animations
    this.startAnimations();

    // Scroll-zoom
    this.setupControls();

    this.setDepth(Depth.panel);
    scene.add.existing(this);
  }

  // ── Full redraw ────────────────────────────────────────────

  private drawAll(): void {
    const g = this.gfx;
    g.clear();

    // 1. Dark overlay on the lower portion (the "radar screen")
    this.drawOverlay(g);

    // 2. Radial grid lines
    this.drawRadials(g);

    // 3. Range rings
    this.drawRangeRings(g);

    // 4. Entities
    this.drawEntities(g);

    // 5. Radar sweep
    this.drawSweep(g);
  }

  // ── Overlay ────────────────────────────────────────────────

  private drawOverlay(g: Phaser.GameObjects.Graphics): void {
    // Fill the lower portion of the circle with a dark overlay.
    // We draw horizontal strips, clipped to the circle, from OVERLAY_Y down.
    g.fillStyle(0x000000, OVERLAY_ALPHA);
    const step = 2;
    for (let y = OVERLAY_Y; y < MAP_SIZE; y += step) {
      // Find circle x-bounds at this y
      const dy = y - MAP_RADIUS;
      const rSq = MAP_RADIUS * MAP_RADIUS - dy * dy;
      if (rSq <= 0) continue;
      const halfW = Math.sqrt(rSq);
      g.fillRect(MAP_RADIUS - halfW, y, halfW * 2, step);
    }
  }

  // ── Radial lines ───────────────────────────────────────────

  private drawRadials(g: Phaser.GameObjects.Graphics): void {
    g.lineStyle(1, RADIAL_COLOR, RADIAL_ALPHA);
    for (let i = 0; i < RADIAL_COUNT; i++) {
      const angle = (i / RADIAL_COUNT) * Math.PI * 2;
      const ex = CX + Math.cos(angle) * DRAW_R;
      const ey = CY + Math.sin(angle) * DRAW_R;
      g.beginPath();
      g.moveTo(CX, CY);
      g.lineTo(ex, ey);
      g.strokePath();
    }
  }

  // ── Range rings ────────────────────────────────────────────

  private drawRangeRings(g: Phaser.GameObjects.Graphics): void {
    const scale = (DRAW_R / VIEWPORT_RANGE) * this.zoomLevel;
    g.lineStyle(1, RING_COLOR, RING_ALPHA);
    for (const hexDist of RING_INTERVALS) {
      const r = hexDist * scale * 1.732;
      if (r > DRAW_R) continue;
      g.strokeCircle(CX, CY, r);
    }
  }

  // ── Entities ───────────────────────────────────────────────

  private drawEntities(g: Phaser.GameObjects.Graphics): void {
    const playerUnits = this.mmoState.getPlayerUnits();
    const playerPlanets = this.mmoState.getPlayerPlanets();

    // Center on the camera's current position (what the player is looking at),
    // falling back to the first player unit, then the first planet.
    let centerQ = 0;
    let centerR = 0;
    const cam = this.scene.scene.get('MMOGameScene')?.cameras?.main;
    if (cam) {
      // Convert camera center pixel to hex coords (axial)
      // Reverse of axialToPixel: x = HEX_WIDTH * (3/4 * q), y = HEX_HEIGHT * (r + q/2) * ISO_Y_SCALE
      // HEX_WIDTH=100, HEX_HEIGHT=86, ISO_Y_SCALE=0.55
      const px = cam.scrollX + cam.width / 2;
      const py = cam.scrollY + cam.height / 2;
      const q = (4 / 3 * px) / 100;
      const yFlat = py / 0.55;
      const r = yFlat / 86 - q / 2;
      centerQ = Math.round(q);
      centerR = Math.round(r);
    } else if (playerUnits.length > 0) {
      centerQ = playerUnits[0].position.q;
      centerR = playerUnits[0].position.r;
    } else if (playerPlanets.length > 0) {
      centerQ = playerPlanets[0].position.q;
      centerR = playerPlanets[0].position.r;
    }

    const scale = (DRAW_R / VIEWPORT_RANGE) * this.zoomLevel;
    const toMini = (q: number, r: number) => hexToPixel(q - centerQ, r - centerR, scale);

    // ── Earth (0,0) ──
    {
      const { mx, my } = toMini(0, 0);
      if (inCircle(mx, my)) {
        g.lineStyle(4, COLOR_EARTH, 0.2);
        g.strokeCircle(mx, my, 7);
        g.fillStyle(COLOR_EARTH, 0.9);
        g.fillCircle(mx, my, 5);
        // Small "E" letter using rects
        g.fillStyle(0xffffff, 0.9);
        g.fillRect(mx - 2, my - 3, 1.5, 6);    // vertical bar
        g.fillRect(mx - 2, my - 3, 4, 1.2);     // top bar
        g.fillRect(mx - 2, my - 0.6, 3, 1.2);   // middle bar
        g.fillRect(mx - 2, my + 1.8, 4, 1.2);   // bottom bar
      }
    }

    // ── Planets ──
    for (const planet of playerPlanets) {
      const { mx, my } = toMini(planet.position.q, planet.position.r);
      if (!inCircle(mx, my)) continue;
      g.fillStyle(COLOR_PLANET, 0.85);
      g.fillCircle(mx, my, ENTITY_DOT);
      g.lineStyle(1, COLOR_PLANET, 0.5);
      g.beginPath();
      g.arc(mx, my, ENTITY_DOT + 2, -0.5, 0.5, false);
      g.strokePath();
    }

    // ── Asteroids ──
    const asteroids = this.mmoState.getAllAsteroids();
    const asteroidClusters = new Map<string, { count: number; mx: number; my: number }>();
    for (const ast of asteroids) {
      const { mx, my } = toMini(ast.position.q, ast.position.r);
      if (!inCircle(mx, my)) continue;

      if (!isPriorityAsteroid(ast.asteroidType)) {
        const key = `${Math.floor(mx / ASTEROID_CLUSTER_CELL)},${Math.floor(my / ASTEROID_CLUSTER_CELL)}`;
        const cluster = asteroidClusters.get(key);
        if (cluster) {
          cluster.count += 1;
          cluster.mx += mx;
          cluster.my += my;
        } else {
          asteroidClusters.set(key, { count: 1, mx, my });
        }
        continue;
      }

      g.fillStyle(COLOR_ASTEROID, 1.0);
      const s = 4;
      g.beginPath();
      g.moveTo(mx, my - s);
      g.lineTo(mx + s, my);
      g.lineTo(mx, my + s);
      g.lineTo(mx - s, my);
      g.closePath();
      g.fillPath();
    }
    const visibleAsteroidClusters = [...asteroidClusters.values()]
      .map((cluster) => {
        const mx = cluster.mx / cluster.count;
        const my = cluster.my / cluster.count;
        const dx = mx - CX;
        const dy = my - CY;
        return { ...cluster, mx, my, distSq: dx * dx + dy * dy };
      })
      .sort((a, b) => (b.count - a.count) || (a.distSq - b.distSq))
      .slice(0, ASTEROID_CLUSTER_MAX_VISIBLE);

    for (const cluster of visibleAsteroidClusters) {
      const mx = cluster.mx;
      const my = cluster.my;
      const radius = Math.min(ASTEROID_CLUSTER_MAX_RADIUS, 2 + Math.sqrt(cluster.count) * 1.15);
      const alpha = Math.min(0.85, 0.38 + cluster.count * 0.035);

      if (cluster.count === 1) {
        g.fillStyle(COLOR_ASTEROID, 0.28);
        g.fillCircle(mx, my, 1.5);
        continue;
      }

      g.fillStyle(COLOR_ASTEROID, alpha);
      g.fillCircle(mx, my, radius);
      g.lineStyle(1, COLOR_ASTEROID, Math.min(0.55, alpha));
      g.strokeCircle(mx, my, radius + 1);
    }

    // ── Stations ──
    const stations = this.mmoState.getAllStations();
    for (const st of stations) {
      const { mx, my } = toMini(st.position.q, st.position.r);
      if (!inCircle(mx, my)) continue;
      g.fillStyle(COLOR_STATION, 0.85);
      g.fillRect(mx - 1, my - 4, 2, 8);
      g.fillRect(mx - 4, my - 1, 8, 2);
    }

    // ── Player units (on top) ──
    const pulse = 1 + this.pulsePhase * 0.4;
    const pAlpha = DOT_GLOW_ALPHA + this.pulsePhase * 0.15;
    for (const unit of playerUnits) {
      const { mx, my } = toMini(unit.position.q, unit.position.r);
      if (!inCircle(mx, my)) continue;
      g.lineStyle(DOT_GLOW_WIDTH * pulse, DOT_COLOR, pAlpha);
      g.strokeCircle(mx, my, (DOT_SIZE / 2) * pulse);
      g.fillStyle(DOT_COLOR, 1.0);
      g.fillCircle(mx, my, DOT_SIZE / 2);
    }

    // Fallback static dot when nothing loaded
    if (playerUnits.length === 0 && playerPlanets.length === 0) {
      g.lineStyle(DOT_GLOW_WIDTH * pulse, DOT_COLOR, pAlpha);
      g.strokeCircle(CX, CY, (DOT_SIZE / 2) * pulse);
      g.fillStyle(DOT_COLOR, 1.0);
      g.fillCircle(CX, CY, DOT_SIZE / 2);
    }
  }

  // ── Radar sweep ────────────────────────────────────────────

  private drawSweep(g: Phaser.GameObjects.Graphics): void {
    // Fading tail
    for (let i = 5; i >= 0; i--) {
      const angle = this.sweepAngle - i * 0.06;
      const alpha = 0.3 - i * 0.05;
      if (alpha <= 0) continue;
      g.lineStyle(i === 0 ? 1.5 : 1, SWEEP_COLOR, alpha);
      g.beginPath();
      g.moveTo(CX, CY);
      g.lineTo(CX + Math.cos(angle) * DRAW_R, CY + Math.sin(angle) * DRAW_R);
      g.strokePath();
    }

    // Leading edge
    g.lineStyle(2, SWEEP_COLOR, 0.4);
    g.beginPath();
    g.moveTo(CX, CY);
    g.lineTo(CX + Math.cos(this.sweepAngle) * DRAW_R, CY + Math.sin(this.sweepAngle) * DRAW_R);
    g.strokePath();
  }

  // ── Animations ─────────────────────────────────────────────

  private startAnimations(): void {
    // Radar sweep rotation
    this.scene.tweens.addCounter({
      from: 0,
      to: Math.PI * 2,
      duration: SWEEP_DURATION,
      repeat: -1,
      onUpdate: (tween) => {
        this.sweepAngle = tween.getValue() ?? 0;
        this.drawAll();
      },
    });

    // Player dot pulse
    this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: (tween) => {
        this.pulsePhase = tween.getValue() ?? 0;
      },
    });
  }

  // ── Legend ──────────────────────────────────────────────────

  private buildLegend(): void {
    this.legendContainer = this.scene.add.container(0, LEGEND_Y);
    this.add(this.legendContainer);

    const items: { label: string; iconKey: string }[] = [
      { label: 'PLANET',   iconKey: 'icon-legend-planet' },
      { label: 'ASTEROID', iconKey: 'icon-legend-asteroid' },
      { label: 'STATION',  iconKey: 'icon-legend-station' },
    ];

    const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '11px',
      color: '#8899BB',
      fontFamily: Fonts.body,
      letterSpacing: 1,
    };

    // Pre-measure to center the row
    const widths: number[] = [];
    for (const item of items) {
      const t = this.scene.add.text(0, 0, item.label, textStyle);
      widths.push(t.width);
      t.destroy();
    }

    const totalW = widths.reduce(
      (sum, w, i) => sum + LEGEND_ICON + LEGEND_GAP + w + (i < items.length - 1 ? LEGEND_SPACING : 0),
      0,
    );
    let cx = (MAP_SIZE - totalW) / 2;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // PNG icon (scaled to LEGEND_ICON × LEGEND_ICON)
      const icon = this.scene.add.image(cx + LEGEND_ICON / 2, 6, item.iconKey);
      icon.setDisplaySize(LEGEND_ICON, LEGEND_ICON);
      icon.setOrigin(0.5, 0.5);
      this.legendContainer.add(icon);

      const lbl = this.scene.add.text(cx + LEGEND_ICON + LEGEND_GAP, 0, item.label, textStyle);
      lbl.setOrigin(0, 0);
      this.legendContainer.add(lbl);

      cx += LEGEND_ICON + LEGEND_GAP + widths[i] + LEGEND_SPACING;
    }
  }

  /**
   * Reposition legend to be vertically centered between the bottom of the
   * minimap circle and the bottom of the available space.
   * @param availableBottom distance from the panel's y origin to the browser bottom
   */
  public setLegendFromBottom(availableBottom: number): void {
    // Bottom of the map circle relative to this container's origin
    const mapBottom = MAP_SIZE;
    const legendH = 16; // approximate legend row height
    const centerY = mapBottom + Math.floor((availableBottom - mapBottom - legendH) / 2);
    this.legendContainer.setY(centerY);
  }

  // ── Controls ───────────────────────────────────────────────

  private setupControls(): void {
    const hitZone = this.scene.add.zone(MAP_RADIUS, MAP_RADIUS, MAP_SIZE, MAP_SIZE)
      .setOrigin(0.5, 0.5);
    hitZone.setInteractive(
      new Phaser.Geom.Circle(MAP_SIZE / 2, MAP_SIZE / 2, MAP_RADIUS),
      Phaser.Geom.Circle.Contains,
    );
    this.add(hitZone);

    hitZone.on('wheel', (_pointer: unknown, _dx: number, deltaY: number) => {
      const d = deltaY > 0 ? -0.1 : 0.1;
      this.zoomLevel = Phaser.Math.Clamp(this.zoomLevel + d, 0.5, 3.0);
    });
  }

  // ── Public API ─────────────────────────────────────────────

  public refresh(): void {
    // Entity positions update; animations handle their own redraws
    // but we also redraw here to catch state changes between animation frames
    this.drawAll();
  }

  public getTotalHeight(): number {
    return MAP_SIZE + 6 + 16;  // circle + 6px gap + legend row
  }
}
